import Vue from 'vue';
import axios, { AxiosInstance } from 'axios';
import { Loading } from 'element-ui';
import { ElLoadingComponent } from 'element-ui/types/loading';

import StorageUtility from '@/classes/common/StorageUtility';




export default class ApiUtility {

  /** APIの基本パス（継承先でこれを変更すれば他のAPIにアクセスできる想定） */
  protected static readonly apiBasePath: string = '/msapi';

  /** 認証/認可トークンキー */
  protected static readonly tokenKey: string = 'token';

  /** クッキートークンのパス（空文字以外なら設定する） */
  protected static readonly cookieTokenPath: string = '';

  /** 400エラーを正常系として処理するか否か */
  protected static readonly isBadRequestResolved: boolean = true;

  /** トークン保存をローカルストレージにするか否か */
  protected static readonly isTokenStoredInLocalStorage: boolean = false;

  /** element-ui loading */
  private static loadingInstance: ElLoadingComponent | null = null;

  /** 通信中リクエスト数 */
  private static connectionCount = 0;




  /**
   * APIの基本パスを取得します
   *
   * @returns
   */
  public static getApiBasePath(): string {
    return this.apiBasePath;
  }

  /**
   * ストレージにトークンが存在するか確認します
   *
   * @return 存在有無
   */
  public static hasToken(): boolean {
    return this.getToken() !== null;
  }

  /**
   * ストレージからトークンを取得します
   *
   * @return token
   */
  public static getToken(): String {
    return this.isTokenStoredInLocalStorage ? StorageUtility.getLocalData<String>(this.tokenKey) : StorageUtility.getSessionData<String>(this.tokenKey);
  }

  /**
   * ストレージにトークンを保存します
   *
   * @param token
   */
  public static saveToken(token: string): void {
    this.isTokenStoredInLocalStorage ? StorageUtility.setLocalData<String>(this.tokenKey, token) : StorageUtility.setSessionData<String>(this.tokenKey, token);
  }

  /**
   * ストレージからトークンを削除します
   */
  public static removeToken(): void {
    this.isTokenStoredInLocalStorage ? localStorage.removeItem(this.tokenKey) : sessionStorage.removeItem(this.tokenKey);
  }


  /**
   * 通信用axiosインスタンスを取得します
   *
   * @param withAuthorization
   * @param withLoading
   * @return AxiosInstance
   */
  protected static getInstance(withAuthorization: boolean = true, withLoading: boolean = true): AxiosInstance {
    const closeLoading = async (): Promise<void> => {
      if (this.connectionCount === 0) {
        await Vue.nextTick();
        if (this.loadingInstance !== null) this.loadingInstance.close();
        this.loadingInstance = null;
      }
    };

    // axios custom instance
    const instance = axios.create({
      timeout: 60000,
      maxRedirects: 0,
      withCredentials: true
    });

    instance.interceptors.request.use(
      (requestConfig) => {
        this.connectionCount++;
        if (withAuthorization) {
          requestConfig.headers.common[this.tokenKey] = this.getToken();
        }
        if (withLoading) {
          this.loadingInstance = Loading.service({
            text: 'Loading',
            spinner: 'el-icon-loading',
            background: 'rgba(0, 0, 0, 0.7)'
          });
        }
        return requestConfig;
      }
    );

    instance.interceptors.response.use(
      (response) => {
        this.connectionCount--;
        closeLoading();
        const token = response.headers[this.tokenKey];
        if (token !== undefined) {
          this.saveToken(token);
          if (this.cookieTokenPath.length > 0)
            document.cookie = `${this.tokenKey}=${token}; path=${this.cookieTokenPath}`;
        }
        return response;
      },
      (error) => {
        this.connectionCount--;
        const responseCode = error.response === undefined ? 0 : error.response.status;
        closeLoading();
        switch (responseCode) {
          // 入力エラーは正常系として処理
          case 400:
            if (this.isBadRequestResolved) return Promise.resolve(error.response);
            break;
            // ログインなど
          case 401:
            if (!withAuthorization) return Promise.resolve(error.response);
            break;
        }

        // unhandledrejection でイベント補足の想定
        return Promise.reject(error);
      },
    );
    return instance;
  }




}
